import React, { useEffect, useState } from "react";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import api from "../../api/axios";
import {
  cleanNumber,
  formatCOPInput,
  formatCurrencyCOP,
} from "../../utils/format";

function EditPurchaseOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [presentations, setPresentations] = useState([]);
  const [orderDate, setOrderDate] = useState("");
  const [supplierId, setSupplierId] = useState(null);
  const [notes, setNotes] = useState("");
  const [details, setDetails] = useState([]);
  const [form, setForm] = useState({
    productId: null,
    presentationId: null,
    quantity: "",
    unitPrice: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productSummary, setProductSummary] = useState({
    currentStock: 0,
    monthlyExits: 0,
  });

  useEffect(() => {
    Promise.all([
      api.get("/suppliers"),
      api.get("/products"),
      api.get("/presentations"),
      api.get(`/purchase-orders/${id}`),
    ])
      .then(([supplierRes, productRes, presentationRes, orderRes]) => {
        setSuppliers(
          (supplierRes.data || []).filter((supplier) => supplier.status !== 0),
        );
        setProducts(productRes.data || []);
        setPresentations(presentationRes.data || []);
        
        const order = orderRes.data;
        setOrderDate(order.order_date || "");
        setSupplierId(order.supplier_id);
        setNotes(order.notes || "");
        
        const loadedDetails = order.details.map((detail) => ({
          productId: detail.product_id,
          presentationId: detail.presentation_id,
          quantity: detail.quantity,
          unitPrice: detail.unit_price,
          productName: detail.product?.name,
          presentationName: detail.presentation?.name,
        }));
        setDetails(loadedDetails);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Error al cargar los datos");
        setLoading(false);
      });
  }, [id]);

  const handleProductChange = async (productId) => {
    setForm((current) => ({
      ...current,
      productId: productId || null,
      presentationId: null,
      unitPrice: "",
    }));
    setErrors((current) => ({
      ...current,
      productId: "",
      presentationId: "",
      unitPrice: "",
    }));
    setProductSummary({ currentStock: 0, monthlyExits: 0 });

    if (!productId) return;

    try {
      const [latestEntryResult, summaryResult] = await Promise.allSettled([
        api.get(`/entries/latest-product/${productId}`),
        api.get(`/kardex/product-summary/${productId}`),
      ]);

      if (summaryResult.status === "fulfilled") {
        setProductSummary({
          currentStock: Number(summaryResult.value.data.current_stock || 0),
          monthlyExits: Number(summaryResult.value.data.monthly_exits || 0),
        });
      }

      if (latestEntryResult.status === "fulfilled") {
        const latestEntry = latestEntryResult.value.data;
        setForm((current) => ({
          ...current,
          productId,
          presentationId: latestEntry.presentation_id ?? latestEntry.presentation?.id ?? null,
          unitPrice: latestEntry.unit_price == null
            ? ""
            : String(Math.round(Number(latestEntry.unit_price))),
        }));
      }

      if (
        latestEntryResult.status === "rejected" &&
        latestEntryResult.reason.response?.status !== 404
      ) {
        toast.error("No se pudo cargar el precio y la presentación anteriores");
      }
    } catch (error) {
      toast.error("No se pudo cargar la información del producto");
    }
  };

  const addDetail = () => {
    const quantity = Number(form.quantity);
    const unitPrice = form.unitPrice === "" ? 0 : Number(form.unitPrice);
    const newErrors = {};

    if (!form.productId) newErrors.productId = "Selecciona un producto";
    if (!form.presentationId) newErrors.presentationId = "Selecciona una presentación";
    if (!Number.isInteger(quantity) || quantity <= 0) {
      newErrors.quantity = "Ingresa una cantidad válida";
    }
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      newErrors.unitPrice = "Ingresa un precio válido";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const product = products.find(
      (item) => Number(item.id) === Number(form.productId),
    );
    const presentation = presentations.find(
      (item) => Number(item.id) === Number(form.presentationId),
    );
    setDetails((current) => [
      ...current,
      {
        ...form,
        quantity,
        unitPrice,
        productName: product?.name,
        presentationName: presentation?.name,
      },
    ]);
    setForm({
      productId: null,
      presentationId: null,
      quantity: "",
      unitPrice: "",
    });
    setErrors({});
  };

  const handlePreSubmit = () => {
    const newErrors = {};
    if (!orderDate) newErrors.orderDate = "Selecciona la fecha de la orden";
    if (!supplierId) newErrors.supplierId = "Selecciona un proveedor";
    if (details.length === 0) newErrors.details = "Agrega al menos un producto";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Corrige los errores antes de guardar");
      return;
    }
    setShowConfirmModal(true);
  };

  const save = async () => {
    setShowConfirmModal(false);
    try {
      setSaving(true);
      await api.put(`/purchase-orders/${id}`, {
        order_date: orderDate,
        notes,
        details: details.map((detail) => ({
          product_id: detail.productId,
          presentation_id: detail.presentationId,
          quantity: detail.quantity,
          unit_price: detail.unitPrice,
        })),
      });
      Swal.fire({
        title: "¡Orden actualizada!",
        text: "La orden de compra fue actualizada correctamente.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        willClose: () => navigate(`/purchase-orders/${id}`),
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "No se pudo actualizar la orden",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="p-6 text-gray-500">Cargando...</p>;

  const total = details.reduce(
    (sum, detail) => sum + detail.quantity * detail.unitPrice,
    0,
  );
  const supplierOptions = suppliers.map((supplier) => ({
    value: supplier.id,
    label: supplier.name,
  }));
  const productOptions = products.map((product) => ({
    value: product.id,
    label: product.name,
  }));
  const presentationOptions = presentations.map((presentation) => ({
    value: presentation.id,
    label: presentation.name,
  }));

  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-7xl mx-auto">
      <button
        onClick={() => navigate(`/purchase-orders/${id}`)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} /> Volver a la orden
      </button>
      <h2 className="text-2xl text-center font-bold mb-6">
        Editar orden de compra
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-semibold">Fecha de la orden</label>
          <input
            type="date"
            value={orderDate}
            onChange={(e) => setOrderDate(e.target.value)}
            className={`w-full mt-1 border rounded px-3 py-2 ${errors.orderDate ? "border-red-500" : "border-gray-300"}`}
          />
          {errors.orderDate && <p className="text-red-500 text-sm mt-1">{errors.orderDate}</p>}
        </div>
        <div>
          <label className="font-semibold">Proveedor</label>
          <Select
            options={supplierOptions}
            value={
              supplierOptions.find((option) => option.value === supplierId) ||
              null
            }
            onChange={(option) => setSupplierId(option?.value || null)}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: errors.supplierId ? "#ef4444" : base.borderColor,
              }),
            }}
            placeholder="Seleccionar proveedor..."
          />
          {errors.supplierId && <p className="text-red-500 text-sm mt-1">{errors.supplierId}</p>}
        </div>
      </div>
      <hr className="my-6" />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div>
          <label className="font-semibold">Producto</label>
          <Select
            options={productOptions}
            value={
              productOptions.find(
                (option) => option.value === form.productId,
              ) || null
            }
            onChange={(option) => handleProductChange(option?.value)}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: errors.productId ? "#ef4444" : base.borderColor,
              }),
            }}
            placeholder="Seleccionar..."
          />
          {errors.productId && <p className="text-red-500 text-sm mt-1">{errors.productId}</p>}
        </div>
        <div>
          <label className="font-semibold">Presentación</label>
          <Select
            options={presentationOptions}
            value={
              presentationOptions.find(
                (option) => option.value === form.presentationId,
              ) || null
            }
            onChange={(option) =>
              setForm({ ...form, presentationId: option?.value || null })
            }
            styles={{
              control: (base) => ({
                ...base,
                borderColor: errors.presentationId ? "#ef4444" : base.borderColor,
              }),
            }}
            placeholder="Seleccionar..."
          />
          {errors.presentationId && <p className="text-red-500 text-sm mt-1">{errors.presentationId}</p>}
        </div>
        <div>
          <label className="font-semibold">Cantidad</label>
          <input
            type="number"
            min="1"
            step="1"
            value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            className={`w-full border rounded px-3 py-2 ${errors.quantity ? "border-red-500" : "border-gray-300"}`}
          />
          {errors.quantity && <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>}
        </div>
        <div>
          <label className="font-semibold">Precio unitario estimado</label>
          <input
            type="text"
            inputMode="numeric"
            min="0"
            value={formatCOPInput(form.unitPrice)}
            onChange={(e) =>
              setForm({ ...form, unitPrice: cleanNumber(e.target.value) })
            }
            className={`w-full border rounded px-3 py-2 ${errors.unitPrice ? "border-red-500" : "border-gray-300"}`}
          />
          {errors.unitPrice && <p className="text-red-500 text-sm mt-1">{errors.unitPrice}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="font-semibold">Cantidad actual del insumo</label>
          <input
            type="text"
            value={productSummary.currentStock.toLocaleString("es-CO")}
            readOnly
            className="w-full mt-1 border rounded px-3 py-2 bg-gray-100 text-gray-600 cursor-not-allowed"
          />
        </div>
        <div>
          <label className="font-semibold">Cantidad salida este mes</label>
          <input
            type="text"
            value={productSummary.monthlyExits.toLocaleString("es-CO")}
            readOnly
            className="w-full mt-1 border rounded px-3 py-2 bg-gray-100 text-gray-600 cursor-not-allowed"
          />
        </div>
      </div>
      <button
        onClick={addDetail}
        className="mt-5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center gap-2"
      >
        <Plus size={18} /> Agregar producto
      </button>
      {errors.details && <p className="text-red-500 text-sm mt-3">{errors.details}</p>}
      <div className="overflow-x-auto rounded-xl shadow mt-6">
        <table className="min-w-full text-center">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-3">Producto</th>
              <th className="p-3">Presentación</th>
              <th className="p-3">Cantidad</th>
              <th className="p-3">Precio</th>
              <th className="p-3">Total</th>
              <th className="p-3">Acción</th>
            </tr>
          </thead>
          <tbody>
            {details.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-6 text-gray-400">
                  No hay productos agregados.
                </td>
              </tr>
            ) : (
              details.map((detail, index) => (
                <tr key={`${detail.productId}-${index}`} className="border-t">
                  <td className="p-3">{detail.productName}</td>
                  <td className="p-3">{detail.presentationName}</td>
                  <td className="p-3">{detail.quantity}</td>
                  <td className="p-3">{formatCurrencyCOP(detail.unitPrice)}</td>
                  <td className="p-3">
                    {formatCurrencyCOP(detail.quantity * detail.unitPrice)}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() =>
                        setDetails((current) =>
                          current.filter((_, itemIndex) => itemIndex !== index),
                        )
                      }
                      className="text-red-600"
                      title="Eliminar"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-5">
        <label className="font-semibold">Notas para el proveedor</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full mt-1 border rounded px-3 py-2"
          rows="3"
        />
      </div>
      <div className="flex justify-between items-center mt-6">
        <strong className="text-lg">
          Total estimado: {formatCurrencyCOP(total)}
        </strong>
        <button
          onClick={handlePreSubmit}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-5 py-2 rounded flex items-center gap-2"
        >
          <Save size={18} /> {saving ? "Guardando..." : "Actualizar orden"}
        </button>
      </div>
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <Save className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                ¿Confirmar cambios?
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Se actualizarán los datos de la orden con {details.length} productos.
              </p>
              <div className="mt-4 space-y-2 p-3 bg-gray-50 rounded-lg text-gray-700">
                <p>
                  <span className="font-semibold">Cantidad total:</span>{" "}
                  {details.reduce((sum, detail) => sum + detail.quantity, 0).toLocaleString("es-CO")}
                </p>
                <p>
                  <span className="font-semibold">Precio estimado total:</span>{" "}
                  {formatCurrencyCOP(total)}
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition"
              >
                Cancelar
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 font-semibold transition shadow-md"
              >
                {saving ? "Guardando..." : "Sí, actualizar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditPurchaseOrder;
